import { useEffect, useMemo, useState } from "react";
import { Button, Chip, Input, Modal, Select, type Column } from "@legal-500/legal500-design-system";
import {
  AI_PARSE_RUN_STATUSES,
  aiParser,
  type AiParseRun,
  type AiParseRunAggregate,
  type AiParseRunProgress,
  type AiParseRunFilters,
} from "@/api/aiParser.service";
import { DataTable } from "@/components/DataTable/DataTable";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { PageHeader } from "@/components/PageHeader";
import { PaginationControls } from "@/components/PaginationControls/PaginationControls";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { useQuery } from "@/hooks/useApi";
import { usePermissions } from "@/hooks/usePermissions";
import { BulkRerunModal } from "./BulkRerunModal";
import { ManualUploadModal } from "./ManualUploadModal";
import styles from "./AiParseRuns.module.css";

const PAGE_SIZE = 20;

const STATUS_CLASS: Record<string, string> = {
  queued: "statusQueued",
  running: "statusRunning",
  succeeded: "statusSucceeded",
  invalid: "statusInvalid",
  failed: "statusFailed",
};

function formatDuration(ms: number | null): string {
  if (ms === null) return "—";
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

/**
 * The run folder and file name — the part of a blob path that says which output this is.
 *
 * Computed rather than clipped with CSS so the visible text is the same everywhere, including in
 * a test and in a copied screenshot; the untruncated path stays available on the title and the
 * copy button.
 */
function blobPathTail(path: string): string {
  const segments = path.split("/");
  if (segments.length <= 2) return path;
  return `…/${segments.slice(-2).join("/")}`;
}

/**
 * Copies a blob path. The path itself is not sensitive — it is a file name in a container this UI
 * cannot read — but it is long enough that retyping it into Azure is where mistakes happen.
 */
function CopyBlobPath({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  async function handleClick() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <button
      type="button"
      className={styles.copyBlobPath}
      onClick={handleClick}
      aria-label={`Copy the full blob path ${value}`}
      title={copied ? "Copied" : "Copy the full path"}
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

/** Queue time is the gap between being enqueued and the worker picking the job up. */
function queueTimeMs(run: AiParseRun): number | null {
  if (!run.startedAt) return null;
  return new Date(run.startedAt).getTime() - new Date(run.queuedAt).getTime();
}

/**
 * estimatedCost is a decimal string end to end (numeric(12,6) in Postgres). It is formatted, not
 * summed — the API already did the summing, in SQL, precisely.
 */
function formatCost(value: string | null): string {
  if (value === null) return "—";
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return value;
  return `$${parsed.toFixed(4)}`;
}

function formatPercent(fraction: number | null): string {
  if (fraction === null) return "—";
  return `${Math.round(fraction * 100)}%`;
}

function fieldsPopulatedPercent(run: AiParseRun): string {
  if (run.fieldsTotal === null || run.fieldsPopulated === null || run.fieldsTotal === 0) return "—";
  return `${Math.round((run.fieldsPopulated / run.fieldsTotal) * 100)}%`;
}

/** An HTML date input gives YYYY-MM-DD; the API wants ISO 8601 carrying an offset. */
function toIsoDate(value: string, endOfDay: boolean): string | undefined {
  if (!value) return undefined;
  return endOfDay ? `${value}T23:59:59.999Z` : `${value}T00:00:00.000Z`;
}

/** Statuses that can still change on their own, so the page keeps asking while one is showing. */
const LIVE_STATUSES = new Set(["queued", "running"]);

function isLive(run: AiParseRun): boolean {
  return LIVE_STATUSES.has(run.status);
}

/**
 * How often the page re-asks while something is running.
 *
 * Matched to the worker, which publishes progress at most every 400ms: polling faster would
 * re-fetch the same numbers, and polling much slower makes a 60-second run look like four
 * updates. The interval only exists while a run is live — a settled page makes no requests.
 */
const LIVE_POLL_MS = 2000;

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
}

function ProgressBar({
  label,
  percent,
  detail,
}: {
  label: string;
  percent: number;
  detail: string;
}) {
  const clamped = clampPercent(percent);
  return (
    <div className={styles.progressBar}>
      <div className={styles.progressLabelRow}>
        <span className={styles.progressLabel}>{label}</span>
        <span className={styles.progressDetail}>{detail}</span>
      </div>
      <div
        className={styles.progressTrack}
        role="progressbar"
        aria-label={label}
        aria-valuenow={Math.round(clamped)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className={styles.progressFill} style={{ width: `${clamped}%` }} />
      </div>
    </div>
  );
}

/**
 * What a run is doing right now.
 *
 * Three bars, because the three things that can be slow are different questions and a single
 * percentage would hide which one you are waiting on:
 *
 * - **Reading** — the document going in. Azure reports prompt tokens only in the stream's final
 *   chunk, so until then this shows the pre-call estimate and completes the moment the first
 *   generated token proves the prompt was read.
 * - **Writing** — output against the input size. A submission's extraction lands in the same
 *   order of magnitude as the document it came from, which makes tokens-out over tokens-in a
 *   usable ratio; it is capped at 100% rather than allowed to overshoot on a verbose run.
 * - **Fields** — the honest one. The partial output is repaired and counted against the schema
 *   exactly as the finished run is, so this number means the same thing before and after the run
 *   ends.
 */
function RunProgress({ run }: { run: AiParseRun }) {
  const progress: AiParseRunProgress | null = run.progress;

  if (!isLive(run)) return <span className={styles.muted}>—</span>;

  if (!progress) {
    return (
      <span className={styles.progressWaiting}>
        {run.status === "queued" ? "Waiting for a worker" : "Starting…"}
      </span>
    );
  }

  const promptTokens = progress.promptTokens ?? progress.estimatedPromptTokens;
  const readingDone = progress.completionTokens > 0 || progress.promptTokens !== null;
  const writingPercent = promptTokens > 0 ? (progress.completionTokens / promptTokens) * 100 : 0;
  const fieldsPercent =
    progress.fieldsPopulated !== null && progress.fieldsTotal > 0
      ? (progress.fieldsPopulated / progress.fieldsTotal) * 100
      : 0;

  return (
    <div className={styles.progressCell}>
      <ProgressBar
        label="Reading"
        percent={readingDone ? 100 : 15}
        detail={`${promptTokens.toLocaleString()}${progress.promptTokens === null ? " est." : ""} tokens`}
      />
      <ProgressBar
        label="Writing"
        percent={writingPercent}
        detail={`${progress.completionTokens.toLocaleString()} tokens${
          progress.tokensPerSecond === null ? "" : ` · ${progress.tokensPerSecond}/s`
        }`}
      />
      <ProgressBar
        label="Fields"
        percent={fieldsPercent}
        detail={
          progress.fieldsPopulated === null
            ? "not parseable yet"
            : `${progress.fieldsPopulated} / ${progress.fieldsTotal}`
        }
      />
      {progress.attempt > 1 && (
        <span className={styles.progressDetail}>Retry {progress.attempt} of 2</span>
      )}
    </div>
  );
}

function AggregateCard({ label, aggregate }: { label: string; aggregate: AiParseRunAggregate }) {
  return (
    <div className={styles.statCard}>
      <p className={styles.statLabel}>{label}</p>
      <div className={styles.statValueRow}>
        <p className={styles.statValue}>{formatPercent(aggregate.successRate)}</p>
        <span className={styles.statPct}>
          {aggregate.succeededRuns}/{aggregate.totalRuns} succeeded
        </span>
      </div>
      <p className={styles.statFootnote}>
        {aggregate.invalidRuns} invalid · {aggregate.failedRuns} failed ·{" "}
        {aggregate.totalTokens.toLocaleString()} tokens · {formatCost(aggregate.totalEstimatedCost)}{" "}
        ·{" "}
        {aggregate.averageDurationMs === null
          ? "no timings"
          : `avg ${formatDuration(Math.round(aggregate.averageDurationMs))}`}
        {aggregate.averageFieldsPopulatedPercent === null
          ? ""
          : ` · ${Math.round(aggregate.averageFieldsPopulatedPercent)}% of fields populated`}
      </p>
    </div>
  );
}

function AiParseRunsPage() {
  const { hasPermission } = usePermissions();
  const canConfigure = hasPermission("submission.config");

  const [schemaFilter, setSchemaFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [publicationFilter, setPublicationFilter] = useState("");
  const [editionFilter, setEditionFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);

  const [rerunTarget, setRerunTarget] = useState<AiParseRun | null>(null);
  const [rerunPending, setRerunPending] = useState(false);
  const [rerunError, setRerunError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const { data: schemasData } = useQuery(() => aiParser.listSchemas(), []);
  const schemas = useMemo(() => schemasData?.schemas ?? [], [schemasData]);

  const filters: AiParseRunFilters = {
    aiSchemaId: schemaFilter || undefined,
    status: statusFilter || undefined,
    publicationName: publicationFilter || undefined,
    edition: editionFilter || undefined,
    queuedFrom: toIsoDate(fromDate, false),
    queuedTo: toIsoDate(toDate, true),
  };

  const filterDeps = [
    schemaFilter,
    statusFilter,
    publicationFilter,
    editionFilter,
    fromDate,
    toDate,
  ];

  const {
    data,
    loading,
    error,
    refetch: refetchRuns,
  } = useQuery(
    () => aiParser.listRuns({ ...filters, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }),
    [...filterDeps, page],
  );

  const { data: statsData, refetch: refetchStats } = useQuery(
    () => aiParser.getRunStats(filters),
    filterDeps,
  );

  /** A rerun changes what the table and the aggregates should say, so both are re-read. */
  async function refreshAll() {
    await Promise.all([refetchRuns(), refetchStats()]);
  }

  const runs = useMemo(() => data?.runs ?? [], [data]);
  const hasLiveRuns = runs.some(isLive);

  // Poll only while something can still change. A page of settled runs is static data and makes
  // no requests at all; the moment a rerun is queued the interval starts again on the next
  // response. refetch() sets isRefetching rather than loading, so the table updates in place
  // instead of flashing a spinner every two seconds.
  useEffect(() => {
    if (!hasLiveRuns) return;
    const timer = window.setInterval(() => {
      void refreshAll();
    }, LIVE_POLL_MS);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLiveRuns, refetchRuns, refetchStats]);
  const total = data?.pagination.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;

  function onFilterChange(apply: () => void) {
    apply();
    setPage(1);
  }

  const schemaById = useMemo(
    () => new Map(schemas.map(schema => [schema.id, schema] as const)),
    [schemas],
  );

  async function handleRerun() {
    if (!rerunTarget) return;
    const name = rerunTarget.aiSchemaName ?? "that schema";
    setRerunPending(true);
    setRerunError(null);
    try {
      const response = await aiParser.rerunRun(rerunTarget.id);
      setNotice(
        response.enqueued
          ? `Queued a rerun of ${name} for that document. It appears as its own row once a worker picks it up — the original run is left exactly as it was.`
          : `The request was accepted but nothing was queued, so no rerun of ${name} is pending.`,
      );
      setRerunTarget(null);
      await refreshAll();
    } catch (caught) {
      setRerunError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setRerunPending(false);
    }
  }

  const columns: Column<AiParseRun>[] = useMemo(
    () => [
      {
        header: "Status",
        accessor: "status",
        render: (_value, row) => (
          <Chip
            size="small"
            variant="filled"
            className={styles[STATUS_CLASS[row.status] ?? "statusQueued"]}
          >
            {isLive(row) && <span className={styles.spinner} aria-hidden="true" />}
            {row.status}
          </Chip>
        ),
      },
      {
        header: "Progress",
        accessor: "id",
        wrap: true,
        render: (_value, row) => <RunProgress run={row} />,
      },
      {
        header: "Schema",
        accessor: "aiSchemaName",
        render: (_value, row) => (
          <div className={styles.stackedCell}>
            <span>{row.aiSchemaName ?? "—"}</span>
            <span className={styles.mutedMono}>{row.model ?? "—"}</span>
          </div>
        ),
      },
      {
        header: "Publication",
        accessor: "publicationName",
        wrap: true,
        // A manual upload belongs to no firm and has no submission, so the publication and edition
        // it was labelled with are not facts about anything — they are notes the uploader typed.
        // The chip and the file name take the column instead, so the row cannot be read as a
        // firm's submission at a glance; the optional labels stay underneath, where they are
        // clearly annotation rather than provenance.
        render: (_value, row) =>
          row.source === "manual" ? (
            <div className={styles.stackedCell}>
              <Chip size="small" variant="filled" className={styles.manualChip}>
                Manual
              </Chip>
              <span className={styles.manualFileName} title={row.manualUploadFileName ?? undefined}>
                {row.manualUploadFileName ?? "uploaded document"}
              </span>
              {(row.publicationName || row.edition) && (
                <span className={styles.muted}>
                  {[row.publicationName, row.edition].filter(Boolean).join(" · ")}
                </span>
              )}
            </div>
          ) : (
            <div className={styles.stackedCell}>
              <span>{row.publicationName ?? "—"}</span>
              <span className={styles.muted}>{row.edition ?? "—"}</span>
            </div>
          ),
      },
      {
        header: "Queued",
        accessor: "queuedAt",
        render: (_value, row) => new Date(row.queuedAt).toLocaleString(),
      },
      {
        header: "Queue time",
        accessor: "startedAt",
        render: (_value, row) => formatDuration(queueTimeMs(row)),
      },
      {
        header: "Duration",
        accessor: "durationMs",
        render: (_value, row) => formatDuration(row.durationMs),
      },
      {
        header: "Tokens",
        accessor: "promptTokens",
        render: (_value, row) => {
          const prompt = row.promptTokens ?? 0;
          const completion = row.completionTokens ?? 0;
          if (row.promptTokens === null && row.completionTokens === null) return "—";
          return (
            <div className={styles.stackedCell}>
              <span>{(prompt + completion).toLocaleString()}</span>
              <span className={styles.muted}>
                {prompt.toLocaleString()} in / {completion.toLocaleString()} out
              </span>
            </div>
          );
        },
      },
      {
        header: "Cost",
        accessor: "estimatedCost",
        render: (_value, row) => formatCost(row.estimatedCost),
      },
      {
        header: "Fields populated",
        accessor: "fieldsPopulated",
        render: (_value, row) => (
          <div className={styles.stackedCell}>
            <span>{fieldsPopulatedPercent(row)}</span>
            <span className={styles.muted}>
              {row.fieldsPopulated ?? "—"} / {row.fieldsTotal ?? "—"}
            </span>
          </div>
        ),
      },
      {
        header: "Validation",
        accessor: "validationErrorCount",
        wrap: true,
        render: (_value, row) => {
          // Null is "not validated yet", which is not the same claim as "validated, no errors" —
          // a running row must not advertise a clean bill of health it has not earned.
          if (row.validationErrorCount === null) return <span className={styles.muted}>—</span>;
          const count = row.validationErrorCount;
          if (count === 0) return <span className={styles.muted}>No errors</span>;
          return (
            <div className={styles.stackedCell}>
              <span className={styles.validationCount}>
                {count} validation error{count === 1 ? "" : "s"}
              </span>
              {/* JSON Pointers only — the path Ajv rejected, never the value that was at it. */}
              {/* Clamped to two lines: a run can fail twenty pointers, and the row must not
                  grow to fit them. The full list is on the hover title. */}
              <span className={styles.pathList} title={(row.validationErrorPaths ?? []).join(", ")}>
                {(row.validationErrorPaths ?? []).join(", ") || "paths not recorded"}
              </span>
            </div>
          );
        },
      },
      {
        header: "Output",
        accessor: "blobPath",
        render: (_value, row) => {
          if (!row.blobPath) return <span className={styles.muted}>—</span>;
          // Deliberately plain text, not a link, and nothing on this page fetches it. The blob
          // container holds the model's extraction of a firm's submission; this page is metrics
          // only, so the path is here purely so someone with Azure access can find the file.
          // Making it clickable would put submission content one click away from a stats screen.
          //
          // Shown as its last two segments. A full path is ~150 characters of UUIDs and wrapped
          // to a row half a screen tall, which buried every other column on the page; the tail is
          // what tells two runs apart, and the whole string is on the hover title and one click
          // away on the copy button.
          return (
            <div className={styles.blobPathCell}>
              <span className={styles.blobPath} title={row.blobPath}>
                {blobPathTail(row.blobPath)}
              </span>
              <CopyBlobPath value={row.blobPath} />
            </div>
          );
        },
      },
      {
        header: "Actions",
        accessor: "id",
        render: (_value, row) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setRerunError(null);
              setNotice(null);
              setRerunTarget(row);
            }}
          >
            Rerun
          </Button>
        ),
      },
    ],
    [],
  );

  if (!canConfigure) {
    return (
      <UnauthorizedView message="You need the submission.config permission to view AI parse runs." />
    );
  }

  const stats = statsData?.stats ?? null;

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader
          title="AI Parse Runs"
          subtitle="Counts, statuses and timings for the AI submission parser — no submission content"
        >
          <Button variant="outlined" onClick={() => setUploadOpen(true)}>
            Upload document
          </Button>
          <Button variant="contained" onClick={() => setBulkOpen(true)}>
            Bulk rerun
          </Button>
        </PageHeader>
      </div>

      <div className={styles.filterSection}>
        <Select
          label="Schema"
          value={schemaFilter}
          onChange={event => onFilterChange(() => setSchemaFilter(event.target.value))}
          options={[
            { value: "", label: "All schemas" },
            ...schemas.map(schema => ({ value: schema.id, label: schema.name })),
          ]}
          size="small"
        />
        <Select
          label="Status"
          value={statusFilter}
          onChange={event => onFilterChange(() => setStatusFilter(event.target.value))}
          options={[
            { value: "", label: "All" },
            ...AI_PARSE_RUN_STATUSES.map(status => ({ value: status, label: status })),
          ]}
          size="small"
        />
        <Input
          label="Publication"
          value={publicationFilter}
          onChange={event => onFilterChange(() => setPublicationFilter(event.target.value))}
          placeholder="Exact publication name"
        />
        <Input
          label="Edition"
          value={editionFilter}
          onChange={event => onFilterChange(() => setEditionFilter(event.target.value))}
          placeholder="e.g. 2027"
        />
        <Input
          label="Queued from"
          type="date"
          value={fromDate}
          onChange={event => onFilterChange(() => setFromDate(event.target.value))}
        />
        <Input
          label="Queued to"
          type="date"
          value={toDate}
          onChange={event => onFilterChange(() => setToDate(event.target.value))}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={() =>
            onFilterChange(() => {
              setSchemaFilter("");
              setStatusFilter("");
              setPublicationFilter("");
              setEditionFilter("");
              setFromDate("");
              setToDate("");
            })
          }
        >
          Clear filters
        </Button>
      </div>

      {stats && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Success rate</h3>
          <div className={styles.statGrid}>
            <AggregateCard label="Overall" aggregate={stats.overall} />
            {stats.bySchema.map(schema => (
              <AggregateCard
                key={schema.aiSchemaId}
                label={schema.aiSchemaName ?? schema.aiSchemaSlug ?? "Unnamed schema"}
                aggregate={schema}
              />
            ))}
          </div>
        </div>
      )}

      <p className={styles.contentNotice}>
        This page never shows submission content. It reports counts, statuses, timings and the
        JSON-Pointer paths that failed validation. The blob path is plain text so someone with Azure
        access can find the output file — it is not a link and nothing here fetches it.
      </p>

      {notice && (
        <div className={styles.noticeBanner} role="status">
          {notice}
        </div>
      )}

      {loading && <LoadingState message="Loading AI parse runs..." />}

      {!loading && error && (
        <div className={styles.errorBanner} role="alert">
          Could not load AI parse runs: {error.message}
        </div>
      )}

      {!loading && !error && runs.length === 0 && (
        <EmptyState
          variant="empty"
          title="No AI parse runs"
          message="No run matches these filters."
        />
      )}

      {!loading && !error && runs.length > 0 && (
        <>
          <DataTable data={runs} columns={columns} rowKey="id" size="small" />
          <PaginationControls
            currentPage={page}
            totalPages={totalPages}
            startIndex={startIndex}
            endIndex={startIndex + runs.length}
            totalCount={total}
            itemName="runs"
            onPageChange={setPage}
          />
        </>
      )}

      <Modal
        isOpen={rerunTarget !== null}
        onClose={() => setRerunTarget(null)}
        title="Rerun this document"
        size="small"
      >
        <div className={styles.modalBody}>
          <p className={styles.confirmText}>
            Re-queue this document against{" "}
            <strong>{rerunTarget?.aiSchemaName ?? "this schema"}</strong>, and only that schema. The
            original run is left exactly as it is — the new attempt gets its own row, so the two can
            be compared.
          </p>
          <p className={styles.confirmNote}>
            Naming a run explicitly is asking for it, so this runs the schema{" "}
            <strong>even if that schema is currently disabled</strong>. A disabled schema is out of
            the rotation, not out of reach.
          </p>
          {rerunTarget && schemaById.get(rerunTarget.aiSchemaId)?.enabled === false && (
            <p className={styles.warnNote}>
              {schemaById.get(rerunTarget.aiSchemaId)?.name} is disabled right now. This one rerun
              will still run and still cost money; it does not put the schema back in the rotation.
            </p>
          )}
          {rerunError && (
            <div className={styles.errorBanner} role="alert">
              {rerunError}
            </div>
          )}
          <div className={styles.formActions}>
            <Button variant="outlined" onClick={() => setRerunTarget(null)} disabled={rerunPending}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleRerun} disabled={rerunPending}>
              {rerunPending ? "Queueing…" : "Rerun"}
            </Button>
          </div>
        </div>
      </Modal>

      <BulkRerunModal
        isOpen={bulkOpen}
        schemas={schemas}
        onClose={() => setBulkOpen(false)}
        onEnqueued={() => {
          void refreshAll();
        }}
      />

      <ManualUploadModal
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={fileName => {
          setNotice(
            `Uploaded ${fileName}. It is being parsed by every enabled schema now — each run appears below on its own, with its progress. The document is attached to no firm and no submission was created for it.`,
          );
          void refreshAll();
        }}
      />
    </div>
  );
}

export default AiParseRunsPage;
